import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, User, Mail, Phone, Globe, Briefcase, Calendar,
  Shield, Check, ChevronRight, ArrowLeft, Loader2, CheckCircle2,
  FileText, AlertCircle, Video
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Package options
const PACKAGES = [
  {
    id: 'recording',
    name: 'Trade Show Booth (Recording)',
    price: 16999,
    description: '50 recordings per day with automatic BrandStage branding',
    features: [
      '50 video recordings per day',
      'Automatic BrandStage intro & outro',
      'Background music',
      'Instant email delivery',
      'Cloudinary hosting'
    ]
  },
  {
    id: 'editing_suite',
    name: 'Trade Show Booth (Editing Suite)',
    price: 89000,
    description: 'Top 10 videos with custom branding and professional editing',
    features: [
      'Everything in Recording package',
      'Professional editing of top 10 videos',
      'Custom branding elements',
      'Social media optimized exports',
      'Priority support'
    ]
  }
];

// Terms and Conditions text
const TERMS_TEXT = `
BRANDSTAGE TRADE SHOW BOOTH - TERMS AND CONDITIONS

1. SERVICE DESCRIPTION
BrandStage provides video recording booth services at trade shows and events, including professional branding of recorded content.

2. PARTICIPANT DECLARATION
By using this service, you confirm that:
- You are authorized to represent your company
- All statements made are accurate to your knowledge
- Content will not be illegal, defamatory, or inappropriate
- BrandStage may edit, brand, and publish recordings
- You indemnify BrandStage against claims arising from your content

3. CONTENT GUIDELINES
Prohibited content includes: illegal activities, fraud, defamation, hate speech, explicit material, misleading claims, unauthorized use of third-party content.

4. BRANDSTAGE RIGHTS
BrandStage reserves the right to:
- Review and reject content
- Remove branding from inappropriate content
- Refuse service without refund for policy violations

5. PAYMENT & REFUNDS
- Payment required before event code activation
- Refunds at BrandStage's discretion
- Unused recordings do not carry over

6. LIABILITY
BrandStage branding does not constitute endorsement of claims made by participants.

Version: 1.0.0 | Effective Date: July 2026
`;

const STEPS = {
  PACKAGE: 'package',
  INFO: 'info',
  DECLARATION: 'declaration',
  CONFIRM: 'confirm',
  SUCCESS: 'success'
};

export default function BookBooth() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.PACKAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Package
    package_type: '',
    // Customer Info
    name: '',
    email: '',
    mobile: '',
    company: '',
    designation: '',
    website: '',
    // Event Details
    event_name: '',
    event_start_date: '',
    event_end_date: '',
    daily_limit: 50
  });
  
  // Declarations
  const [declarations, setDeclarations] = useState({
    terms_accepted: false,
    authority: false,
    accuracy: false,
    prohibited: false,
    rights: false,
    indemnity: false,
    data_processing: false
  });
  
  const [formErrors, setFormErrors] = useState({});
  
  // Validation
  const validateInfo = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Required';
    if (!formData.email.trim()) errors.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email';
    if (!formData.mobile.trim()) errors.mobile = 'Required';
    if (!formData.company.trim()) errors.company = 'Required';
    if (!formData.designation.trim()) errors.designation = 'Required';
    if (!formData.event_name.trim()) errors.event_name = 'Required';
    if (!formData.event_start_date) errors.event_start_date = 'Required';
    if (!formData.event_end_date) errors.event_end_date = 'Required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const allDeclarationsAccepted = Object.values(declarations).every(v => v);
  
  // Submit booking
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        ...formData,
        terms_accepted: declarations.terms_accepted,
        declaration_authority: declarations.authority,
        declaration_accuracy: declarations.accuracy,
        declaration_prohibited: declarations.prohibited,
        declaration_rights: declarations.rights,
        declaration_indemnity: declarations.indemnity
      };
      axios.post(`${API_URL}/api/consents`, { kind: 'book_booth_data_processing', details: { email: formData.email, organisation: formData.company } }).catch(() => {});
      
      const res = await axios.post(`${API_URL}/api/bookings/create`, payload);
      setBookingId(res.data.booking_id);
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setLoading(false);
    }
  };
  
  const selectedPackage = PACKAGES.find(p => p.id === formData.package_type);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>
      
      {/* Progress Steps */}
      {step !== STEPS.SUCCESS && (
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-center gap-2">
            {[
              { id: STEPS.PACKAGE, label: 'Package' },
              { id: STEPS.INFO, label: 'Details' },
              { id: STEPS.DECLARATION, label: 'Agreement' },
              { id: STEPS.CONFIRM, label: 'Confirm' }
            ].map((s, i) => (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-2 ${
                  step === s.id ? 'text-[#C41E3A]' : 
                  [STEPS.INFO, STEPS.DECLARATION, STEPS.CONFIRM].indexOf(step) > [STEPS.INFO, STEPS.DECLARATION, STEPS.CONFIRM].indexOf(s.id) 
                    ? 'text-green-500' : 'text-zinc-600'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step === s.id ? 'bg-[#C41E3A]' : 
                    [STEPS.INFO, STEPS.DECLARATION, STEPS.CONFIRM].indexOf(step) > [STEPS.INFO, STEPS.DECLARATION, STEPS.CONFIRM].indexOf(s.id)
                      ? 'bg-green-500' : 'bg-zinc-800'
                  }`}>
                    {i + 1}
                  </div>
                  <span className="hidden sm:inline text-sm">{s.label}</span>
                </div>
                {i < 3 && <ChevronRight className="w-4 h-4 text-zinc-600" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pb-12">
        
        {/* Step 1: Package Selection */}
        {step === STEPS.PACKAGE && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2">Book Trade Show Booth</h1>
              <p className="text-zinc-400">Select your package to get started</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {PACKAGES.map(pkg => (
                <div
                  key={pkg.id}
                  onClick={() => setFormData({ ...formData, package_type: pkg.id })}
                  className={`bg-zinc-900/50 border-2 rounded-2xl p-6 cursor-pointer transition-all ${
                    formData.package_type === pkg.id
                      ? 'border-[#C41E3A] bg-[#C41E3A]/5'
                      : 'border-zinc-800 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{pkg.name}</h3>
                      <p className="text-zinc-400 text-sm mt-1">{pkg.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      formData.package_type === pkg.id
                        ? 'border-[#C41E3A] bg-[#C41E3A]'
                        : 'border-zinc-600'
                    }`}>
                      {formData.package_type === pkg.id && <Check className="w-4 h-4" />}
                    </div>
                  </div>
                  
                  <p className="text-3xl font-bold mb-4">
                    ₹{pkg.price.toLocaleString()}
                    <span className="text-zinc-500 text-sm font-normal"> + GST</span>
                  </p>
                  
                  <ul className="space-y-2">
                    {pkg.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-green-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => formData.package_type && setStep(STEPS.INFO)}
              disabled={!formData.package_type}
              className={`w-full mt-8 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center gap-2 ${
                formData.package_type
                  ? 'bg-[#C41E3A] hover:bg-[#A01830] text-white'
                  : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              }`}
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
        
        {/* Step 2: Information */}
        {step === STEPS.INFO && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2">Your Details</h1>
              <p className="text-zinc-400">Tell us about you and your event</p>
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              {/* Contact Info */}
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#C41E3A]" />
                Contact Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full bg-zinc-800 border ${formErrors.name ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-2.5`}
                    placeholder="John Smith"
                  />
                  {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Designation *</label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    className={`w-full bg-zinc-800 border ${formErrors.designation ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-2.5`}
                    placeholder="Marketing Director"
                  />
                  {formErrors.designation && <p className="text-red-500 text-xs mt-1">{formErrors.designation}</p>}
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className={`w-full bg-zinc-800 border ${formErrors.company ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-2.5`}
                    placeholder="Acme Inc."
                  />
                  {formErrors.company && <p className="text-red-500 text-xs mt-1">{formErrors.company}</p>}
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Company Website</label>
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5"
                    placeholder="www.company.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full bg-zinc-800 border ${formErrors.email ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-2.5`}
                    placeholder="john@company.com"
                  />
                  {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Mobile *</label>
                  <input
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    className={`w-full bg-zinc-800 border ${formErrors.mobile ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-2.5`}
                    placeholder="+91 98765 43210"
                  />
                  {formErrors.mobile && <p className="text-red-500 text-xs mt-1">{formErrors.mobile}</p>}
                </div>
              </div>
              
              {/* Event Details */}
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C41E3A]" />
                Event Details
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm text-zinc-400 mb-1">Event Name *</label>
                  <input
                    type="text"
                    value={formData.event_name}
                    onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                    className={`w-full bg-zinc-800 border ${formErrors.event_name ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-2.5`}
                    placeholder="Tech Summit 2026"
                  />
                  {formErrors.event_name && <p className="text-red-500 text-xs mt-1">{formErrors.event_name}</p>}
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={formData.event_start_date}
                    onChange={(e) => setFormData({ ...formData, event_start_date: e.target.value })}
                    className={`w-full bg-zinc-800 border ${formErrors.event_start_date ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-2.5`}
                  />
                  {formErrors.event_start_date && <p className="text-red-500 text-xs mt-1">{formErrors.event_start_date}</p>}
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={formData.event_end_date}
                    onChange={(e) => setFormData({ ...formData, event_end_date: e.target.value })}
                    className={`w-full bg-zinc-800 border ${formErrors.event_end_date ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-2.5`}
                  />
                  {formErrors.event_end_date && <p className="text-red-500 text-xs mt-1">{formErrors.event_end_date}</p>}
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Daily Recording Limit</label>
                  <select
                    value={formData.daily_limit}
                    onChange={(e) => setFormData({ ...formData, daily_limit: parseInt(e.target.value) })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5"
                  >
                    <option value={50}>50 recordings/day</option>
                    <option value={100}>100 recordings/day (+₹5,000)</option>
                    <option value={200}>200 recordings/day (+₹12,000)</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Video Guidelines Note */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mt-6 flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-zinc-300">
                  <span className="font-semibold text-white">Tip:</span> Review our{' '}
                  <a href="/video-guidelines" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Video Quality Guidelines</a>
                  {' '}to ensure your booth recordings meet professional standards.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setStep(STEPS.PACKAGE)}
                className="px-8 py-4 bg-zinc-800 hover:bg-zinc-700 rounded-xl font-bold transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => validateInfo() && setStep(STEPS.DECLARATION)}
                className="flex-1 py-4 bg-[#C41E3A] hover:bg-[#A01830] rounded-xl font-bold transition-colors flex items-center justify-center gap-2"
              >
                Continue to Agreement
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
        
        {/* Step 3: Declaration */}
        {step === STEPS.DECLARATION && (
          <div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#C41E3A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#C41E3A]" />
              </div>
              <h1 className="text-3xl font-bold mb-2">Terms & Declaration</h1>
              <p className="text-zinc-400">Please read and accept the following</p>
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 mb-6">
              {/* Terms and Conditions */}
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C41E3A]" />
                Terms and Conditions
              </h3>
              
              <div className="bg-zinc-800/50 rounded-lg p-4 max-h-64 overflow-y-auto text-sm text-zinc-300 whitespace-pre-wrap mb-4">
                {TERMS_TEXT}
              </div>
              
              <label className="flex items-center gap-3 cursor-pointer group">
                <div>
                  <input
                    type="checkbox"
                    checked={declarations.terms_accepted}
                    onChange={(e) => setDeclarations({ ...declarations, terms_accepted: e.target.checked })}
                    className="sr-only"
                  />
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                    declarations.terms_accepted ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'
                  }`}>
                    {declarations.terms_accepted && <Check className="w-4 h-4" />}
                  </div>
                </div>
                <span className="text-sm">I have read and agree to the Terms and Conditions</span>
              </label>
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#C41E3A]" />
                Participant Declaration
              </h3>
              
              <div className="space-y-4">
                {[
                  { key: 'authority', label: 'Authority to Represent', desc: `I am authorized to speak on behalf of ${formData.company} and make statements in recordings.` },
                  { key: 'accuracy', label: 'Content Accuracy', desc: 'All statements, claims, and information provided will be accurate to the best of my knowledge.' },
                  { key: 'prohibited', label: 'No Prohibited Content', desc: 'Recordings will not contain illegal, defamatory, misleading, fraudulent, or inappropriate content.' },
                  { key: 'rights', label: 'Rights & Editorial Control', desc: 'BrandStage may edit, brand, and publish recordings. BrandStage may review, reject, or remove content.' },
                  { key: 'indemnity', label: 'Indemnification', desc: 'I agree to indemnify BrandStage against any claims arising from the content of recordings.' },
                  { key: 'data_processing', label: 'Privacy & Data Processing', desc: 'I consent to BrandStage storing and processing my organisation\'s and participants\' contact details to deliver this booking, as described in the Privacy Policy (DPDP Act 2023 / GDPR).' }
                ].map(item => (
                  <label key={item.key} className="flex items-start gap-3 cursor-pointer group">
                    <div className="mt-0.5">
                      <input
                        type="checkbox"
                        checked={declarations[item.key]}
                        onChange={(e) => setDeclarations({ ...declarations, [item.key]: e.target.checked })}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        declarations[item.key] ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'
                      }`}>
                        {declarations[item.key] && <Check className="w-3 h-3" />}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">{item.label}</span>
                      <p className="text-zinc-400 text-sm">{item.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setStep(STEPS.INFO)}
                className="px-8 py-4 bg-zinc-800 hover:bg-zinc-700 rounded-xl font-bold transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => allDeclarationsAccepted && setStep(STEPS.CONFIRM)}
                disabled={!allDeclarationsAccepted}
                className={`flex-1 py-4 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 ${
                  allDeclarationsAccepted
                    ? 'bg-[#C41E3A] hover:bg-[#A01830] text-white'
                    : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                }`}
              >
                {allDeclarationsAccepted ? 'Continue to Confirmation' : 'Please accept all terms'}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
        
        {/* Step 4: Confirmation */}
        {step === STEPS.CONFIRM && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2">Confirm Your Booking</h1>
              <p className="text-zinc-400">Review your details before submitting</p>
            </div>
            
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-400">{error}</span>
              </div>
            )}
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 mb-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Package */}
                <div>
                  <h3 className="font-bold text-[#C41E3A] mb-3">Package</h3>
                  <p className="text-xl font-bold">{selectedPackage?.name}</p>
                  <p className="text-2xl font-bold mt-2">₹{selectedPackage?.price.toLocaleString()} <span className="text-zinc-500 text-sm">+ GST</span></p>
                </div>
                
                {/* Event */}
                <div>
                  <h3 className="font-bold text-[#C41E3A] mb-3">Event</h3>
                  <p className="font-bold">{formData.event_name}</p>
                  <p className="text-zinc-400">{formData.event_start_date} to {formData.event_end_date}</p>
                  <p className="text-zinc-400">{formData.daily_limit} recordings/day</p>
                </div>
                
                {/* Contact */}
                <div>
                  <h3 className="font-bold text-[#C41E3A] mb-3">Contact</h3>
                  <p className="font-bold">{formData.name}</p>
                  <p className="text-zinc-400">{formData.designation}</p>
                  <p className="text-zinc-400">{formData.email}</p>
                  <p className="text-zinc-400">{formData.mobile}</p>
                </div>
                
                {/* Company */}
                <div>
                  <h3 className="font-bold text-[#C41E3A] mb-3">Company</h3>
                  <p className="font-bold">{formData.company}</p>
                  {formData.website && <p className="text-zinc-400">{formData.website}</p>}
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  All terms and declarations accepted
                </div>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
              <h4 className="font-bold text-amber-300 mb-2">What happens next?</h4>
              <ol className="text-amber-200 text-sm space-y-1 list-decimal list-inside">
                <li>Our team will contact you within 24 hours</li>
                <li>Complete payment via bank transfer or UPI</li>
                <li>Receive your unique event access code via email</li>
                <li>You are ready to record at your event!</li>
              </ol>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => setStep(STEPS.DECLARATION)}
                className="px-8 py-4 bg-zinc-800 hover:bg-zinc-700 rounded-xl font-bold transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 py-4 bg-[#C41E3A] hover:bg-[#A01830] rounded-xl font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Booking Request
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Step 5: Success */}
        {step === STEPS.SUCCESS && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Booking Request Submitted!</h1>
            <p className="text-xl text-zinc-400 mb-8">
              Thank you for your interest in BrandStage Trade Show Booth.
            </p>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 max-w-md mx-auto mb-8">
              <p className="text-zinc-400 mb-2">Booking Reference</p>
              <p className="font-mono text-lg font-bold text-[#C41E3A]">{bookingId}</p>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 max-w-lg mx-auto mb-8">
              <h3 className="font-bold text-blue-300 mb-3">What happens next?</h3>
              <ol className="text-blue-200 text-sm space-y-2 text-left list-decimal list-inside">
                <li>You will receive a confirmation email shortly</li>
                <li>Our team will contact you within 24 hours with payment details</li>
                <li>Once payment is confirmed, you will receive your event access code</li>
                <li>Use the code at <strong>brandstage-media.com/booth</strong> during your event</li>
              </ol>
            </div>
            
            <button
              onClick={() => navigate('/')}
              className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-8 py-4 rounded-xl font-bold transition-colors"
            >
              Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
